import { Card, CardContent, Typography, SxProps, Theme, Box, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
interface StatCardProps {
  title: string;
  value: string | number;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

export const StatCard = ({ title, value, sx = {}, onClick }: StatCardProps) => (
  <Card
    onClick={onClick}
    sx={{
      height: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 1,
      boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': {
        transform: onClick ? 'translateY(-3px)' : 'none',
        boxShadow: onClick ? '0 4px 12px rgba(0,0,0,0.08)' : '0 0 0 1px rgba(0,0,0,0.08)',
      },
      ...sx,
    }}
  >
    <CardContent
      sx={{
        textAlign: 'center',
        p: 2,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '0.5px',
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
          letterSpacing: '1px',
          fontWeight: 700,
          textTransform: 'uppercase',
          mt: 0.5,
          fontSize: '0.8rem',
        }}
      >
        {title}
      </Typography>
    </CardContent>
  </Card>
);

interface DashboardStatsGridProps {
  data: {
    totalOffers: number;
    runningOffers: number;
    upcomingOffers: number;
  };
  loading?: boolean;
  environment: 'staging' | 'prod';
}

export const DashboardStatsGrid = ({ data, loading = false, environment }: DashboardStatsGridProps) => {
  const navigate = useNavigate();

  const handleStatClick = (status?: string) => {
    if (status) {
      navigate(`/offers?sync_on=${environment}&status=${status}`);
    } else {
      navigate(`/offers?sync_on=${environment}`);
    }
  };
  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' } }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  const stats = [
    { title: 'Total Offers', value: `${data.totalOffers}`, onClick: () => handleStatClick() },
    { title: 'Running Offers', value: `${data.runningOffers}`, onClick: () => handleStatClick('running') },
    { title: 'Upcoming Offers', value: `${data.upcomingOffers}`, onClick: () => handleStatClick('upcoming') },
    { title: 'Total TMC', value: 0, onClick: undefined },
    { title: 'Total AB Testing', value: 0, onClick: undefined },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' } }}>
      {stats.map((stat) => (
        <StatCard key={stat.title} title={stat.title} value={stat.value} onClick={stat.onClick} />
      ))}
    </Box>
  );
};
